'use client';

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router';
import {
  setApiAccessToken,
  type UserProfile,
} from '@/lib/api';
import { useActiveBranch } from '@/hooks/use-active-branch';

/**
 * FE-22: cùng nguồn base URL với `lib/api.ts` — đường tương đối chỉ đúng khi
 * web và api cùng origin (mô hình mặc định qua Caddy). Không import từ
 * `lib/api.ts` (biến module-level `API_URL` không export) để tránh vòng phụ
 * thuộc không cần thiết; đọc thẳng `import.meta.env` giống cách `lib/api.ts` làm.
 */
const API_URL = import.meta.env.VITE_API_URL || '';

interface AuthContextValue {
  user: UserProfile | null;
  loading: boolean;
  /**
   * Gọi sau khi login form nhận response từ `/api/auth/login`. Lưu access token vào memory
   * + cập nhật user state. KHÔNG đụng tới refresh token (đã ở httpOnly cookie).
   */
  setAuth: (payload: { accessToken: string; user: UserProfile }) => void;
  /** `allDevices = true` vô hiệu hoá mọi phiên trên mọi thiết bị (ném nếu hỏng). */
  logout: (allDevices?: boolean) => Promise<void>;
  refreshUser: () => Promise<void>;
  hasPermission: (module: string, action: string) => boolean;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * One-time cleanup: xóa tokens cũ trong localStorage từ pattern trước đây.
 * Chạy đúng 1 lần per session để giảm bề mặt XSS.
 */
function cleanupLegacyTokens(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  } catch {
    // ignore quota / disabled storage
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const bootstrappedRef = useRef(false);
  const { activeBranchId } = useActiveBranch();
  // 'ALL' hoặc chưa chọn → null. Mirror PermissionsGuard backend: khi không
  // chọn chi nhánh, quyền gắn chi nhánh VẪN hiệu lực miễn user thuộc chi
  // nhánh đó (app tài xế /m không có branch switcher).
  const branchFilter =
    activeBranchId && activeBranchId !== 'ALL' ? activeBranchId : null;

  /**
   * Bootstrap on mount: cleanup legacy tokens + thử silent refresh qua cookie httpOnly.
   * - Nếu refresh thành công → set state user + access token in-memory.
   * - Nếu fail → user = null, các trang protected sẽ tự redirect /login (middleware
   *   cũng đã chặn ở edge).
   */
  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    cleanupLegacyTokens();

    let cancelled = false;
    (async () => {
      try {
        let res = await fetch(`${API_URL}/api/v1/auth/session/refresh`, {
          method: 'POST',
          credentials: 'include',
          cache: 'no-store',
        });
        // Lỗi tạm thời (503/5xx — BE restart, mạng chập chờn): thử lại 1 lần
        // sau 2s trước khi coi như chưa login — tránh đá tài xế về màn login
        // chỉ vì mở app đúng lúc hạ tầng hắt hơi.
        if (!res.ok && res.status !== 401 && res.status !== 403 && !cancelled) {
          await new Promise((r) => setTimeout(r, 2000));
          res = await fetch(`${API_URL}/api/v1/auth/session/refresh`, {
            method: 'POST',
            credentials: 'include',
            cache: 'no-store',
          });
        }
        if (!res.ok) {
          if (!cancelled) setLoading(false);
          return;
        }
        const data = (await res.json()) as {
          accessToken: string;
          user: UserProfile;
        };
        if (cancelled) return;
        setApiAccessToken(data.accessToken);
        setUser(data.user);
      } catch {
        // network error — coi như chưa login
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const setAuth = useCallback(
    ({ accessToken, user: nextUser }: { accessToken: string; user: UserProfile }) => {
      setApiAccessToken(accessToken);
      setUser(nextUser);
      setLoading(false);
    },
    [],
  );

  /**
   * `allDevices` gọi `session/logout-all` — backend đẩy `sessionEpoch` nên mọi
   * phiên khác (điện thoại, máy ở nhà, cookie đã bị chép ra ngoài) chết ngay.
   * Logout thường chỉ xoá cookie máy này, cố ý: người dùng đa thiết bị không
   * muốn đăng xuất trên điện thoại là mất luôn phiên ở máy văn phòng.
   *
   * Khác biệt quan trọng: `logout-all` đòi Bearer nên phải qua `apiFetch`, và
   * nếu nó hỏng thì KHÔNG được im lặng — người dùng tưởng đã cắt hết phiên mà
   * thực ra chưa. Logout thường thì best-effort là đúng.
   */
  const logout = useCallback(async (allDevices = false) => {
    if (allDevices) {
      const { apiFetch } = await import('@/lib/api');
      // Cố ý KHÔNG bọc try/catch: lỗi phải nổi lên để UI báo thất bại.
      await apiFetch('/api/v1/auth/session/logout-all', { method: 'POST' });
    } else {
      try {
        await fetch(`${API_URL}/api/v1/auth/session/logout`, {
          method: 'POST',
          credentials: 'include',
          cache: 'no-store',
        });
      } catch {
        // best effort — vẫn clear state ngay cả khi network fail
      }
    }
    setApiAccessToken(null);
    setUser(null);
    try {
      // Không để user kế tiếp trên cùng máy kế thừa chi nhánh của người trước
      localStorage.removeItem('vgt.activeBranchId');
    } catch {
      // ignore disabled storage
    }
    navigate('/login', { replace: true });
  }, [navigate]);

  /**
   * Re-fetch profile sau khi đổi thông tin cá nhân. Dùng `apiFetch` để tự retry
   * khi access token expire giữa chừng.
   */
  const refreshUser = useCallback(async () => {
    try {
      const { apiFetch } = await import('@/lib/api');
      const profile = await apiFetch<UserProfile>('/api/v1/auth/profile');
      setUser(profile);
    } catch {
      // token có thể đã expire và refresh fail — apiFetch đã dispatch session-expired,
      // listener bên dưới sẽ xử lý
    }
  }, []);

  // Listen session-expired event từ apiFetch (khi 401 + refresh fail)
  useEffect(() => {
    const handler = () => {
      setApiAccessToken(null);
      setUser(null);
      navigate('/login', { replace: true });
    };
    window.addEventListener('vgt:session-expired', handler);
    return () => window.removeEventListener('vgt:session-expired', handler);
  }, [navigate]);

  // Quyền có thể bị admin đổi giữa phiên (gán role, sửa permission) — refetch
  // profile khi quay lại tab, throttle 30s, để menu/nút cập nhật không cần F5.
  const isLoggedIn = !!user;
  const lastProfileFetchRef = useRef(0);
  useEffect(() => {
    if (!isLoggedIn) return;
    const maybeRefresh = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastProfileFetchRef.current < 30_000) return;
      lastProfileFetchRef.current = now;
      void refreshUser();
    };
    window.addEventListener('focus', maybeRefresh);
    document.addEventListener('visibilitychange', maybeRefresh);
    return () => {
      window.removeEventListener('focus', maybeRefresh);
      document.removeEventListener('visibilitychange', maybeRefresh);
    };
  }, [isLoggedIn, refreshUser]);

  const hasPermission = useCallback(
    (module: string, action: string) => {
      if (!user) return false;
      if (user.roles.some((r) => r.name === 'SUPER_ADMIN')) return true;
      // Mirror PermissionsGuard: quyền toàn cục (branchId == null) luôn hiệu
      // lực; đứng chi nhánh cụ thể → chỉ quyền đúng chi nhánh đó; không chọn
      // chi nhánh ('ALL') → quyền gắn chi nhánh vẫn tính nếu user thuộc chi
      // nhánh đó.
      return user.permissions.some(
        (p) =>
          p.module === module &&
          p.action === action &&
          (p.branchId == null ||
            p.branchId === branchFilter ||
            (branchFilter == null &&
              user.branches.some((b) => b.id === p.branchId))),
      );
    },
    [user, branchFilter],
  );

  const hasRole = useCallback(
    (role: string) => {
      if (!user) return false;
      return user.roles.some((r) => r.name === role);
    },
    [user],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        setAuth,
        logout,
        refreshUser,
        hasPermission,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
